import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import { map } from 'rxjs/operators';
import {Page, Pageable} from 'src/app/model/rest/base';
import {
  ONE_SEARCH_TYPES,
  OneSearchAnyEntry,
  OneSearchManagerEntry,
  OneSearchPeoplePage,
  OneSearchPersonEntry,
  OneSearchPersonView,
  OneSearchSchoolsPage,
  OneSearchStudentEntry,
  OneSearchTeacherEntry,
  OneSearchType,
} from 'src/app/model/v3/one-search';
import { V3School } from 'src/app/model/v3/schools';
import {environment} from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AdminRestService {
  private apiEndpoint = environment.eventsApiBase;
  private readonly defaultDashboardTypes: OneSearchType[] = [
    ONE_SEARCH_TYPES.school,
    ONE_SEARCH_TYPES.person,
    ONE_SEARCH_TYPES.student,
    ONE_SEARCH_TYPES.teacher,
    ONE_SEARCH_TYPES.manager,
    ONE_SEARCH_TYPES.group,
  ];

  constructor(private http: HttpClient) {}

  // ONE SEARCH
  searchDashboardTable(
    query: string,
    pageable?: Pageable,
    types?: OneSearchType[]
  ): Observable<Page<OneSearchAnyEntry>> {
    const page = pageable?.page ?? 0;
    const size = Math.min(pageable?.size ?? 10, 15);
    const dashboardPageable = new Pageable(page, size, pageable?.sort || []);
    const effectiveTypes = types?.length ? types : this.defaultDashboardTypes;
    return this.searchOne(query, effectiveTypes, dashboardPageable);
  }

  searchDashboard(
    query: string,
    pageable?: Pageable
  ): Observable<{ people: OneSearchPeoplePage; schools: OneSearchSchoolsPage }> {
    return this.searchDashboardTable(query, pageable).pipe(
      map(pageRes => {
        const schools: OneSearchSchoolsPage = {
          ...pageRes,
          content: pageRes.content
            .filter(entry => this.resolveEntryType(entry) === ONE_SEARCH_TYPES.school)
            .map(entry => entry as OneSearchSchoolsPage['content'][number]),
        };
        const people = this.toPeoplePage(pageRes);
        return { people, schools };
      })
    );
  }

  private searchOne(
    query: string,
    types: OneSearchType[],
    pageable?: Pageable
  ): Observable<Page<OneSearchAnyEntry>> {
    const normalizedQuery = query?.trim();
    if (!normalizedQuery || normalizedQuery.length < 3) {
      return of(Page.empty<OneSearchAnyEntry>());
    }

    let params = Pageable.appedPageableParams(new HttpParams(), pageable);
    params = params.append('query', normalizedQuery);
    types.forEach(type => {
      params = params.append('types', type);
    });
    return this.http.get<Page<OneSearchAnyEntry>>(
      `${this.apiEndpoint}/v1/people/search/one-search`,
      { params }
    );
  }

  private toPeoplePage(page: Page<OneSearchAnyEntry>): OneSearchPeoplePage {
    const byPersonId = new Map<number, OneSearchPersonView>();

    const getOrCreate = (person: OneSearchPersonEntry | OneSearchStudentEntry['person'] | OneSearchTeacherEntry['person'] | OneSearchManagerEntry['person']): OneSearchPersonView | null => {
      const personId = person?.id;
      if (!personId) return null;
      const existing = byPersonId.get(personId);
      if (existing) return existing;
      const created: OneSearchPersonView = {
        person,
        managerOf: [],
        studentOf: [],
        teacherOf: [],
      };
      byPersonId.set(personId, created);
      return created;
    };

    const appendRole = (
      roles: OneSearchPersonView['managerOf'],
      school: V3School,
      status?: string,
      expiryDate?: string
    ) => {
      if (!school?.id) return;
      const exists = roles.some(role => role.school?.id === school.id && role.role?.status === status && role.role?.expiryDate === expiryDate);
      if (exists) return;
      roles.push({
        school,
        role: { status, expiryDate },
      });
    };

    page.content.forEach(entry => {
      switch (this.resolveEntryType(entry)) {
        case ONE_SEARCH_TYPES.person: {
          const personView = getOrCreate(entry as OneSearchPersonEntry);
          if (personView) personView.person = entry as OneSearchPersonEntry;
          break;
        }
        case ONE_SEARCH_TYPES.student: {
          const studentEntry = entry as OneSearchStudentEntry;
          const personView = getOrCreate(studentEntry.person);
          if (!personView) break;
          appendRole(personView.studentOf, studentEntry.school, studentEntry.status as string, studentEntry.expiryDate);
          break;
        }
        case ONE_SEARCH_TYPES.teacher: {
          const teacherEntry = entry as OneSearchTeacherEntry;
          const personView = getOrCreate(teacherEntry.person);
          if (!personView) break;
          appendRole(personView.teacherOf, teacherEntry.school, teacherEntry.status as string);
          break;
        }
        case ONE_SEARCH_TYPES.manager: {
          const managerEntry = entry as OneSearchManagerEntry;
          const personView = getOrCreate(managerEntry.person);
          if (!personView) break;
          appendRole(personView.managerOf, managerEntry.school);
          break;
        }
      }
    });

    const people = Array.from(byPersonId.values()).sort((a, b) => {
      const aName = `${a.person?.details?.name ?? ''} ${a.person?.details?.surname ?? ''}`.trim();
      const bName = `${b.person?.details?.name ?? ''} ${b.person?.details?.surname ?? ''}`.trim();
      return aName.localeCompare(bName);
    });

    return {
      ...page,
      content: people,
      totalElements: people.length,
      numberOfElements: people.length,
      totalPages: people.length > 0 ? 1 : 0,
    };
  }

  private resolveEntryType(entry: OneSearchAnyEntry | null | undefined): OneSearchType | null {
    return entry?.['@type'] ?? null;
  }
}
